import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CreditCard, Calendar, CheckCircle, Clock, AlertCircle, ExternalLink, X } from 'lucide-react'
import { useSubscription, useSubscriptionActions, useCheckout, formatPrice } from '../../hooks/useBilling'

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; label: string; icon: React.ReactNode }> = {
    active: { bg: 'bg-emerald-100 dark:bg-emerald-900/40', text: 'text-emerald-700 dark:text-emerald-300', label: 'Actif', icon: <CheckCircle className="h-4 w-4" /> },
    trialing: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300', label: 'Période d\'essai', icon: <Clock className="h-4 w-4" /> },
    past_due: { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-300', label: 'Paiement en retard', icon: <AlertCircle className="h-4 w-4" /> },
    canceled: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300', label: 'Annulé', icon: <AlertCircle className="h-4 w-4" /> },
  }
  const c = config[status] || config.active
  return (
    <span className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${c.bg} ${c.text}`}>
      {c.icon}
      {c.label}
    </span>
  )
}

function CancelModal({ open, onClose, onConfirm, loading }: { open: boolean; onClose: () => void; onConfirm: (immediate: boolean) => void; loading: boolean }) {
  if (!open) return null
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-zinc-900">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">Annuler l'abonnement ?</h3>
          <button onClick={onClose} className="rounded-full p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800">
            <X className="h-5 w-5 text-zinc-500" />
          </button>
        </div>
        
        <p className="mb-6 text-sm text-zinc-600 dark:text-zinc-400">
          Êtes-vous sûr de vouloir annuler votre abonnement ? Vous conserverez l'accès jusqu'à la fin de votre période de facturation actuelle.
        </p>
        
        <div className="flex flex-col gap-3">
          <button
            onClick={() => onConfirm(false)}
            disabled={loading}
            className="w-full rounded-full bg-red-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-red-600 disabled:opacity-50"
          >
            {loading ? 'Annulation...' : 'Annuler à la fin de la période'}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="w-full rounded-full border border-zinc-300 px-5 py-2.5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
          >
            Garder mon abonnement
          </button>
        </div>
      </div>
    </div>
  )
}

export default function AccountSubscription() {
  const { subscription, hasSubscription, loading, refetch, daysUntilRenewal, isCanceled } = useSubscription()
  const { cancelSubscription, reactivateSubscription, loading: actionLoading, error: actionError } = useSubscriptionActions()
  const { openBillingPortal, loading: portalLoading } = useCheckout()
  const [showCancelModal, setShowCancelModal] = useState(false)

  const handleCancel = async (immediate: boolean) => {
    try {
      await cancelSubscription(!immediate)
      setShowCancelModal(false)
      refetch()
    } catch (err) {
      console.error('Failed to cancel:', err)
    }
  }

  const handleReactivate = async () => {
    try {
      await reactivateSubscription()
      refetch()
    } catch (err) {
      console.error('Failed to reactivate:', err)
    }
  }

  const handleManagePayment = () => {
    openBillingPortal(window.location.href)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
      </div>
    )
  }

  if (!hasSubscription || !subscription) {
    return (
      <div className="space-y-6 text-center py-8">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-800">
          <CreditCard className="h-8 w-8 text-zinc-400" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">Aucun abonnement actif</h2>
          <p className="mt-2 text-zinc-500">Choisissez un plan pour débloquer toutes les fonctionnalités.</p>
        </div>
        <Link
          to="/pricing"
          className="inline-flex rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600"
        >
          Voir les offres
        </Link>
      </div>
    )
  }

  const periodStart = subscription.current_period_end ? new Date(subscription.current_period_end) : null
  const periodProgress = daysUntilRenewal !== null && periodStart 
    ? Math.max(0, 100 - (daysUntilRenewal / 30) * 100)
    : 0

  return (
    <div className="space-y-6">
      {actionError && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700 dark:bg-red-950/40 dark:border-red-900/60 dark:text-red-200">
          {actionError}
        </div>
      )}

      {/* Plan Info */}
      <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6 dark:border-zinc-700 dark:bg-zinc-800/50">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-zinc-900 dark:text-white">{subscription.plan_name}</h2>
            <p className="mt-1 text-zinc-500">
              {subscription.billing_interval === 'year' ? 'Facturation annuelle' : 'Facturation mensuelle'}
            </p>
          </div>
          <StatusBadge status={subscription.status} />
        </div>
      </div>

      {/* Period Info */}
      <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6 dark:border-zinc-700 dark:bg-zinc-800/50">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="h-5 w-5 text-emerald-500" />
          <h3 className="font-semibold text-zinc-900 dark:text-white">Période de facturation</h3>
        </div>
        
        <div className="space-y-4">
          {subscription.current_period_end && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">
                  {isCanceled ? 'Accès jusqu\'au' : 'Prochain renouvellement'}
                </span>
                <span className="font-medium text-zinc-900 dark:text-white">
                  {new Date(subscription.current_period_end).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
              
              {daysUntilRenewal !== null && (
                <div>
                  <div className="mb-2 flex justify-between text-xs text-zinc-500">
                    <span>Progression</span>
                    <span>{daysUntilRenewal} jours restants</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all" 
                      style={{ width: `${periodProgress}%` }} 
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {subscription.trial_end && subscription.status === 'trialing' && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 dark:bg-amber-950/40 dark:border-amber-900/60">
              <p className="text-sm text-amber-700 dark:text-amber-200">
                <Clock className="mr-2 inline h-4 w-4" />
                Période d'essai jusqu'au {new Date(subscription.trial_end).toLocaleDateString('fr-FR')}
              </p>
            </div>
          )}

          {isCanceled && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 dark:bg-amber-950/40 dark:border-amber-900/60">
              <p className="text-sm text-amber-700 dark:text-amber-200">
                <AlertCircle className="mr-2 inline h-4 w-4" />
                Votre abonnement sera annulé à la fin de la période. Vous conservez l'accès jusqu'à cette date.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Link
          to="/pricing"
          className="rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600"
        >
          Changer de plan
        </Link>
        
        <button
          onClick={handleManagePayment}
          disabled={portalLoading}
          className="rounded-full border border-zinc-300 bg-white px-5 py-2.5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
        >
          <ExternalLink className="mr-2 inline h-4 w-4" />
          {portalLoading ? 'Chargement...' : 'Gérer le paiement'}
        </button>

        {isCanceled ? (
          <button
            onClick={handleReactivate}
            disabled={actionLoading}
            className="rounded-full border border-emerald-300 bg-emerald-50 px-5 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
          >
            {actionLoading ? 'Réactivation...' : 'Réactiver l\'abonnement'}
          </button>
        ) : (
          <button
            onClick={() => setShowCancelModal(true)}
            className="rounded-full border border-red-300 bg-white px-5 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50 dark:border-red-800 dark:bg-transparent dark:text-red-400 dark:hover:bg-red-950/40"
          >
            Annuler l'abonnement
          </button>
        )}
      </div>

      <CancelModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancel}
        loading={actionLoading}
      />
    </div>
  )
}
